import { useCallback, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { dbui_v1_Notable } from '@antimatterhq/asl-api-ts-v1';
import { produce } from 'immer';
import { Text } from '@antimatterhq/ui/components/typography';
import { Badge } from '@antimatterhq/ui/components/badge';
import { formatDate } from '@antimatterhq/ui/utils/format';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import Spacer from '@antimatterhq/ui/components/spacer';
import { CodeEditor } from '@antimatterhq/ui/components/codeEditor';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import { isAxiosError } from 'axios';
import { PaginationInDetailsView } from '../_partials/PaginationInDetailsView';
import { AssignToButton } from '../_partials/AssignToButton';
import { UpdateNotablesStateSelect } from '../_partials/UpdateNotablesStateSelect';
import { getStatusText, getBadgeVariant } from '../utils';
import { cleanObject } from '@/utils/util';
import SimpleInputDialog from '@/components/dialog/simpleInput/SimpleInputDialog';
import { useGetNotableById } from '@/hooks/dbui/useGetNotableById';
import { BackLink } from '@/components/link/back/BackLink';
import useNotables from '@/hooks/notables/useNotables';
import { capitalize } from '@/utils/capitalize';

export const NotableDetailsSkeleton = () => {
  return (
    <div>
      <div className="flex w-full flex-row items-center justify-between">
        <BackLink fallbackRoute="/explore/notables" />
        <div className="flex flex-row gap-2">
          <>
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-32" />
            <div className="ml-2">
              <Skeleton className="h-8 w-24" />
            </div>
          </>
        </div>
      </div>

      <Spacer size="base" />

      <div>
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex gap-2">
            <Skeleton className="h-6 w-16" />
            <Skeleton className="h-6 w-28" />
          </div>
        </div>

        <Spacer size="lg" />

        <div className="w-[800px]">
          <div className="flex justify-between">
            <div className="grid w-full grid-cols-2 gap-2">
              <div className="flex flex-row gap-2">
                <Text bold>Notable ID:</Text>
                <Skeleton className="h-4 w-24" />
              </div>

              <div className="flex flex-row gap-2">
                <Text bold>Assignee: </Text>
                <Skeleton className="h-4 w-24" />
              </div>

              <div className="flex flex-row gap-2">
                <Text bold>First event time:</Text>
                <Skeleton className="h-4 w-24" />
              </div>

              <div className="flex flex-row gap-2">
                <Text bold>Number of matching events:</Text>
                <Skeleton className="h-4 w-24" />
              </div>

              <div className="flex flex-row gap-2">
                <Text bold>Severity:</Text>
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          </div>

          <Spacer size="lg" />

          <div>
            <Text bold>Context:</Text>
            <Spacer size="sm" />
            <Skeleton className="h-60" />
          </div>

          <div>
            <Text bold>Observables:</Text>
            <Spacer size="sm" />
            <Skeleton className="h-60" />
          </div>

          <div>
            <Text bold>Collated events:</Text>
            <Spacer size="sm" />
            <Skeleton className="h-60" />
          </div>

          <div>
            <Text bold>Rule specification:</Text>
            <Spacer size="sm" />
            <Skeleton className="h-60" />
          </div>
        </div>
      </div>
    </div>
  );
};

export const NotableDetails = () => {
  const match = useMatch('/explore/notables/:id');
  const { id = '' } = match?.params || {};
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const navigate = useNavigate();

  const {
    data: notable,
    isLoading,
    isFetching,
    isPending,
    error,
    refetch,
  } = useGetNotableById(id);

  const {
    optimisticUpdateNotableById,
    updateNotableAssignee,
    updateNotableState,
    isUpdatingNotables,
  } = useNotables();

  const optimisticUpdateNotableAssigneeById = useCallback(
    async (assignee: string) => {
      await updateNotableAssignee(assignee, [id], () => {
        if (!notable) return;
        const newData = produce(notable, (draft) => {
          if (draft) {
            draft.assignedTo = assignee;
          }
        });
        optimisticUpdateNotableById(newData);
      });
    },
    [updateNotableAssignee, id, notable, optimisticUpdateNotableById],
  );

  const optimisticUpdateNotableStateById = useCallback(
    async (state: dbui_v1_Notable['state']) => {
      await updateNotableState(state, [id], () => {
        if (!notable) return;
        const newData = produce(notable, (draft) => {
          if (draft) {
            draft.state = state;
          }
        });
        optimisticUpdateNotableById(newData);
      });
    },
    [updateNotableState, id, notable, optimisticUpdateNotableById],
  );

  if (isLoading || isFetching || isPending) {
    return <NotableDetailsSkeleton />;
  }

  if (error) {
    const errorMessage = isAxiosError(error)
      ? error.response?.data.message
      : (error.message ?? 'Please try again later.');

    const is404 = isAxiosError(error) && error.response?.status === 404;

    return (
      <GlobalError
        iconSize="sm"
        title={is404 ? 'Notable not found' : 'Unable to load notable'}
        retryText={is404 ? 'Return to list' : undefined}
        description={capitalize(errorMessage)}
        onRetry={async () => {
          if (is404) {
            navigate('/explore/notables');
          } else {
            await refetch();
          }
        }}
      />
    );
  }

  if (!notable) {
    return (
      <GlobalError
        iconSize="sm"
        title="Notable not found"
        retryText="Return to list"
        onRetry={async () => {
          navigate('/explore/notables');
        }}
      />
    );
  }

  const cleaned = cleanObject(notable);

  return (
    <div>
      <div className="flex w-full flex-row items-center justify-between">
        <BackLink fallbackRoute="/explore/notables" />
        <div className="flex flex-row gap-2">
          <>
            <AssignToButton
              disabled={isUpdatingNotables}
              onClick={() => setShowAssignDialog(true)}
            />
            <UpdateNotablesStateSelect
              disabled={isUpdatingNotables}
              onSelect={(value) =>
                optimisticUpdateNotableStateById(
                  value as dbui_v1_Notable['state'],
                )
              }
            />
            <div className="ml-2">
              <PaginationInDetailsView currentId={id} />
            </div>
          </>
        </div>
      </div>

      <Spacer size="base" />

      <div>
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex gap-2">
            <Badge variant="filled" color={getBadgeVariant(cleaned?.state)}>
              {getStatusText(cleaned?.state)}
            </Badge>
            <Text size="xl" bold>
              {cleaned?.summary}
            </Text>
          </div>
        </div>

        <Spacer size="lg" />

        <div className="w-[800px]">
          <div className="flex justify-between">
            <div className="grid w-full grid-cols-2 gap-2">
              <div className="flex flex-row gap-2">
                <Text bold>Notable ID:</Text>
                <Text>{cleaned?.id}</Text>
              </div>

              <div className="flex flex-row gap-2">
                <Text bold>Assignee: </Text>
                <Text>{cleaned?.assignedTo || 'none'}</Text>
              </div>

              <div className="flex flex-row gap-2">
                <Text bold>First event time:</Text>
                <Text>{formatDate(cleaned?.collate?.firstEventTime)}</Text>
              </div>

              <div className="flex flex-row gap-2">
                <Text bold>Number of matching events:</Text>
                <Text>{cleaned?.collate?.numberOfMatchingEvents}</Text>
              </div>

              <div className="flex flex-row gap-2">
                <Text bold>Severity:</Text>
                <Text>{cleaned?.severity}</Text>
              </div>
            </div>
          </div>

          <Spacer size="lg" />

          <div>
            <Text bold>Context:</Text>
            <Spacer size="sm" />
            <CodeEditor
              code={JSON.stringify(cleaned?.context, null, 2)}
              readOnly
            />
          </div>

          <div>
            <Text bold>Observables:</Text>
            <Spacer size="sm" />
            <CodeEditor
              code={JSON.stringify(cleaned?.observables, null, 2)}
              readOnly
            />
          </div>

          <div>
            <Text bold>Collated events:</Text>
            <Spacer size="sm" />
            <CodeEditor
              code={JSON.stringify(cleaned?.collate, null, 2)}
              readOnly
            />
          </div>

          <div>
            <Text bold>Rule specification:</Text>
            <Spacer size="sm" />
            <CodeEditor
              code={JSON.stringify(cleaned?.rule, null, 2)}
              readOnly
            />
          </div>
        </div>
      </div>

      <SimpleInputDialog
        open={showAssignDialog}
        onOpenChange={setShowAssignDialog}
        onSubmit={(value) => optimisticUpdateNotableAssigneeById(value)}
        title="Assign to"
      />
    </div>
  );
};
